import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DashboardFooter } from "../dashboard/layout/DashboardFooter";
import { DashboardHeader } from "../dashboard/layout/DashboardHeader";
import { ZeosButton } from "../../components/Shared/ZeosButton";
import { ZeosInput } from "../../components/Shared/ZeosInput";
import { AuthContext } from "../../context/AuthContext";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { createLogger } from "../../services/Logger";
import { getSearchParamNumber } from "../../services/nav/QueryStringHelpers";
import { ICompanyClaims } from "../../services/zeos-api/contracts/Account";
import { IProjectAiSummary, IProjectUpdateRequest } from "../../services/zeos-api/contracts/Project";

type ProjectPath = "automatic" | "manual";
type ProjectStep =
  | "identify"
  | "automatic"
  | "manual"
  | "memory"
  | "recommendations";

const PROJECT_TYPES = [
  "Solar",
  "Battery",
  "EV Charging",
  "Efficiency",
  "HVAC",
  "Lighting",
  "Envelope",
  "Other",
];

const MAX_UPLOAD_MB = 20;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".pdf", ".xls", ".xlsx", ".csv", ".doc", ".docx"];
const PROJECT_DATA_VERSION = 1;
const CONTENT_TYPE_BY_EXT: Record<string, string> = {
  ".pdf": "application/pdf",
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

interface IProjectProposalUploadProps {
  projectId: number;
  propertyId: number;
  onUploaded: (file: IProjectUploadMetadata) => Promise<void> | void;
}

interface IProjectUploadMetadata {
  fileName: string;
  sizeBytes: number;
  contentType: string;
  storageKey: string;
  uploadedAt: string;
}

const ProjectProposalUpload = ({
  projectId,
  propertyId,
  onUploaded,
}: IProjectProposalUploadProps) => {
  const log = createLogger("ProjectProposalUpload");
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const isDisabled = useMemo(() => {
    return !file || isUploading;
  }, [file, isUploading]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStatus(null);
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    } else {
      setFile(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setStatus(null);
    setError(null);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const uploadToS3 = async (
    uploadUrl: string,
    fileToUpload: File,
    contentType: string
  ) => {
    const putResponse = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: fileToUpload,
    });

    if (!putResponse.ok) {
      throw new Error(`Upload failed with status ${putResponse.status}`);
    }
  };

  const handleUpload = async () => {
    setStatus(null);
    setError(null);

    if (!file) {
      setError("Choose a file to upload.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`File must be ${MAX_UPLOAD_MB} MB or smaller.`);
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase();
    const extWithDot = ext ? `.${ext}` : "";
    if (extWithDot && !ALLOWED_EXTENSIONS.includes(extWithDot)) {
      setError("Allowed file types: PDF, XLS, XLSX, CSV, DOC, DOCX.");
      return;
    }

    setIsUploading(true);
    try {
      const contentType =
        CONTENT_TYPE_BY_EXT[extWithDot] ||
        file.type ||
        "application/octet-stream";
      const projectIdString = projectId.toString();
      const propertyIdString = propertyId.toString();
      const user = await auth.zeosUserAsync();

      log.info("Proposal upload starting", {
        projectId,
        propertyId,
        fileName: file.name,
      });

      const uploadRes = await api.Proposals.createUploadUrl({
        projectId: projectIdString,
        propertyId: propertyIdString,
        fileName: file.name,
        contentType,
        fileSizeBytes: file.size,
      });

      if (!uploadRes?.data?.uploadUrl) {
        throw new Error(uploadRes?.message || "Could not create upload URL.");
      }

      await uploadToS3(uploadRes.data.uploadUrl, file, contentType);

      await api.Proposals.requestProposal({
        projectId: projectIdString,
        propertyId: propertyIdString,
        fileKey: uploadRes.data.key,
        fileName: file.name,
        requestedBy: user?.zeosUserId?.toString(),
        requestedByEmail: user?.emailAddress,
        source: "ui",
      });

      setStatus("Proposal uploaded and queued.");
      await onUploaded({
        fileName: file.name,
        sizeBytes: file.size,
        contentType,
        storageKey: uploadRes.data.key,
        uploadedAt: new Date().toISOString(),
      });
      setFile(null);
      log.info("Proposal upload succeeded", { projectId, propertyId });
    } catch (err: any) {
      log.error("Proposal upload failed", err);
      setError(err?.message || "Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        Max size {MAX_UPLOAD_MB} MB · PDF/XLS/XLSX/CSV/DOC/DOCX
      </div>
      <div className="flex items-center gap-3">
        <label
          htmlFor="project-upload-input"
          className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm text-gray-600"
        >
          {file?.name || "Choose the files to Upload"}
        </label>
        <label
          htmlFor="project-upload-input"
          className="px-4 py-2 rounded bg-zeos-orange-md text-white hover:bg-zeos-orange-dk transition-all duration-300 cursor-pointer"
        >
          Browse
        </label>
        <input
          id="project-upload-input"
          type="file"
          accept={ALLOWED_EXTENSIONS.join(",")}
          onChange={onFileChange}
          className="hidden"
        />
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-sm text-gray-500 ${
          isDragging ? "border-zeos-orange-md bg-orange-50" : "border-gray-200"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className="text-2xl">☁</div>
        <div className="mt-2">Drag and Drop Files Here</div>
      </div>
      <ZeosButton
        label={isUploading ? "Uploading..." : "Upload Files"}
        type="button"
        mode="action"
        onPress={handleUpload}
        isDisabled={isDisabled}
      />

      {status && (
        <div className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
          {status}
        </div>
      )}
      {error && (
        <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
    </div>
  );
};

export const ProjectStartScreen = () => {
  const log = createLogger("ProjectStartScreen");
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const propertyId = getSearchParamNumber("property", searchParams);
  const companyId = getSearchParamNumber("company", searchParams);
  const forceAiSummary =
    searchParams.get("forceAiSummary") === "true" ||
    searchParams.get("forceAiSummary") === "1";

  const [activeCompany, setActiveCompany] = useState<ICompanyClaims>();
  const [propertyName, setPropertyName] = useState("Property");
  const [step, setStep] = useState<ProjectStep>("identify");
  const [path, setPath] = useState<ProjectPath>("automatic");
  const [projectId, setProjectId] = useState<number | null>(null);
  const [projectName, setProjectName] = useState("");
  const [projectType, setProjectType] = useState(PROJECT_TYPES[0]);
  const [projectDescription, setProjectDescription] = useState("");
  const [manualCostEstimate, setManualCostEstimate] = useState("");
  const [manualConfidence, setManualConfidence] = useState("");
  const [manualSystemSize, setManualSystemSize] = useState("");
  const [manualYearInstalled, setManualYearInstalled] = useState("");
  const [manualExpectedProduction, setManualExpectedProduction] = useState("");
  const [manualBuildings, setManualBuildings] = useState("");
  const [manualIncentives, setManualIncentives] = useState("");
  const [manualUsefulLife, setManualUsefulLife] = useState("");
  const [manualPerformanceGoal, setManualPerformanceGoal] = useState("");
  const [manualAnnualSavings, setManualAnnualSavings] = useState("");
  const [manualSimplePayback, setManualSimplePayback] = useState("");
  const [manualRoiIrr, setManualRoiIrr] = useState("");
  const [manualExpectedSystemLife, setManualExpectedSystemLife] = useState("");
  const [manualEstimatedAnnualSavings, setManualEstimatedAnnualSavings] =
    useState("");
  const [manualOptionalRoiIrr, setManualOptionalRoiIrr] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<IProjectUploadMetadata[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState<IProjectAiSummary | null>(null);
  const [aiSummaryError, setAiSummaryError] = useState<string | null>(null);

  useEffect(() => {
    const initAsync = async () => {
      if (!propertyId) {
        return;
      }
      const response = await api.Property.getProperty(propertyId);
      if (response?.data?.propertyName) {
        setPropertyName(response.data.propertyName);
      }
    };
    initAsync();
  }, [api.Property, propertyId]);

  useEffect(() => {
    if (step !== "recommendations" || !projectId) {
      return;
    }

    let isActive = true;

    const loadSummaryAsync = async () => {
      if (isActive) {
        await fetchAiSummaryAsync(projectId);
      }
    };

    loadSummaryAsync();

    return () => {
      isActive = false;
    };
  }, [projectId, step, forceAiSummary]);

  const breadcrumb = useMemo(() => {
    const stepLabelByStep: Record<ProjectStep, string> = {
      identify: "Add Project",
      automatic: "Add Project > Automatic Data",
      manual: "Add Project > Manual Data",
      memory: "ZEOS Project Memory",
      recommendations: "ZEOS Recommendations",
    };
    const stepLabel = stepLabelByStep[step];
    if (!propertyId) {
      return `Properties > ${stepLabel}`;
    }
    return `Properties > ${propertyName} > ${stepLabel}`;
  }, [propertyId, propertyName, step]);

  const canContinue =
    !busy && Boolean(propertyId) && projectName.trim().length > 0;

  const parseAiSummary = (raw?: string | null): IProjectAiSummary | null => {
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as { aiSummary?: IProjectAiSummary };
      return parsed?.aiSummary ?? null;
    } catch {
      return null;
    }
  };

  const fetchAiSummaryAsync = async (targetProjectId: number) => {
    setAiSummaryError(null);
    try {
      const response = await api.Projects.getProjectById(
        targetProjectId,
        forceAiSummary
      );
      if (response?.message === "error" || !response?.data) {
        throw new Error(response?.message || "Unable to load project summary.");
      }
      const summary = parseAiSummary(response.data.projectDataJson);
      setAiSummary(summary);
      log.info("AI summary fetched", {
        projectId: targetProjectId,
        status: summary?.status,
      });
    } catch (err: any) {
      log.error("Failed to load AI summary", err);
      setAiSummaryError(err?.message || "Unable to load AI summary.");
    }
  };

  const handleBackToIncentives = () => {
    if (propertyId) {
      navigate(`/incentives?property=${propertyId}`);
      return;
    }
    navigate("/incentives");
  };

  const handleBackToProjects = () => {
    const params = new URLSearchParams();
    if (propertyId) {
      params.set("property", String(propertyId));
    }
    if (companyId != null) {
      params.set("company", String(companyId));
    }
    const query = params.toString();
    navigate(`/projects${query ? `?${query}` : ""}`);
  };

  const createProjectStub = async () => {
    if (!propertyId) {
      setError("Select a property before starting a project.");
      return null;
    }
    if (!projectName.trim()) {
      setError("Enter a project name.");
      return null;
    }

    setError(null);
    setBusy(true);
    try {
      const user = await auth.zeosUserAsync();
      const response = await api.Projects.createProject({
        propertyId,
        projectName,
        projectType,
        description: projectDescription,
        source: path === "automatic" ? "proposal" : "manual",
        createdBy: user?.zeosUserId ?? 0,
        createdByEmail: user?.emailAddress ?? "",
      });

      if (response?.message === "error" || !response?.data) {
        throw new Error(response?.message || "Unable to create project.");
      }

      log.info("Project stub created", {
        projectId: response.data.projectId,
        propertyId,
        source: path,
      });
      return response.data.projectId ?? null;
    } catch (err: any) {
      log.error("Project stub creation failed", err);
      setError(err?.message || "Unable to create project.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = async () => {
    const existingProjectId = projectId ?? (await createProjectStub());
    if (!existingProjectId) {
      return;
    }
    setProjectId(existingProjectId);
    const nextStep = path === "automatic" ? "automatic" : "manual";
    log.info("Project flow continue", { step: nextStep, projectId });
    setStep(nextStep);
  };

  const handleAnalyzeData = () => {
    const shouldSkipMemory = path === "automatic" && uploadedFiles.length > 0;
    log.info("Analyze data (UI-only)", {
      projectId,
      step,
      shouldSkipMemory,
      uploads: uploadedFiles.length,
    });
    setStep(shouldSkipMemory ? "recommendations" : "memory");
  };

  const handleCompleteSurvey = async () => {
    log.info("Project memory complete", { projectId });
    await persistProjectDataJson(undefined, "memory-complete");
    setStep("recommendations");
  };


  const buildProjectDataPayload = (uploads: IProjectUploadMetadata[]) => {
    return {
      version: PROJECT_DATA_VERSION,
      projectData: {
        systemSize: manualSystemSize.trim(),
        yearInstalled: manualYearInstalled.trim(),
        expectedProduction: manualExpectedProduction.trim(),
        buildings: manualBuildings.trim(),
        costStack: {
          totalInstalledCost: manualCostEstimate.trim(),
          incentivesOrRebates: manualIncentives.trim(),
        },
        performanceIntent: {
          primaryGoal: manualPerformanceGoal.trim(),
          expectedUsefulLifeYears: manualUsefulLife.trim(),
        },
        economicOutcomes: {
          annualEnergySavings: manualAnnualSavings.trim(),
          simplePayback: manualSimplePayback.trim(),
          roiIrr: manualRoiIrr.trim(),
        },
        optional: {
          expectedSystemLife: manualExpectedSystemLife.trim(),
          estimatedAnnualSavings: manualEstimatedAnnualSavings.trim(),
          roiIrr: manualOptionalRoiIrr.trim(),
        },
        uploads: uploads.map((uploadedFile) => ({
          fileName: uploadedFile.fileName,
          sizeBytes: uploadedFile.sizeBytes,
          contentType: uploadedFile.contentType,
          storageKey: uploadedFile.storageKey,
          uploadedAt: uploadedFile.uploadedAt,
        })),
      },
    };
  };

  const persistProjectDataJson = async (
    uploadsOverride?: IProjectUploadMetadata[],
    reason?: string
  ) => {
    if (!projectId) {
      return;
    }

    try {
      const user = await auth.zeosUserAsync();
      const uploads = uploadsOverride ?? uploadedFiles;
      const payload = buildProjectDataPayload(uploads);
      const projectDataJson = JSON.stringify(payload);

      log.info("Project data payload built", {
        projectId,
        version: PROJECT_DATA_VERSION,
        uploads: uploads.length,
        reason,
      });

      const request: IProjectUpdateRequest = {
        updatedBy: user?.zeosUserId ?? 0,
        projectDataJson,
        projectDataVersion: PROJECT_DATA_VERSION,
      };

      const response = await api.Projects.updateProject(projectId, request);
      if (response?.message === "error") {
        throw new Error(response?.message || "Unable to update project data.");
      }

      log.info("Project data persisted", { projectId, uploads: uploads.length });
    } catch (err: any) {
      log.error("Project data persistence failed", err);
      setError(err?.message || "Unable to save project data.");
    }
  };

  const handleUploadMetadataPersist = async (
    uploadedFile: IProjectUploadMetadata
  ) => {
    const nextUploads = [...uploadedFiles, uploadedFile];
    setUploadedFiles(nextUploads);
    log.info("Upload metadata recorded", {
      projectId,
      fileName: uploadedFile.fileName,
      storageKey: uploadedFile.storageKey,
    });
    await persistProjectDataJson(nextUploads, "automatic-upload");
  };

  const handleManualSubmit = async () => {
    if (!projectId) {
      setError("Project not created yet.");
      return;
    }

    setError(null);

    const hasEconomicOutcome =
      manualAnnualSavings.trim() ||
      manualSimplePayback.trim() ||
      manualRoiIrr.trim();

    if (!hasEconomicOutcome) {
      setError("Enter at least one Economic Outcome value.");
      log.info("Manual data validation failed", { projectId });
      return;
    }

    setBusy(true);
    try {
      const user = await auth.zeosUserAsync();
      const totalCostEstimate = manualCostEstimate.trim()
        ? Number(manualCostEstimate)
        : null;
      const confidencePercentage = manualConfidence.trim()
        ? Number(manualConfidence)
        : null;

      if (totalCostEstimate !== null && Number.isNaN(totalCostEstimate)) {
        setError("Total Cost Estimate must be a number.");
        setBusy(false);
        return;
      }
      if (confidencePercentage !== null && Number.isNaN(confidencePercentage)) {
        setError("Confidence Percentage must be a number.");
        setBusy(false);
        return;
      }

      const payload = buildProjectDataPayload(uploadedFiles);
      log.info("Project data payload built", {
        projectId,
        version: PROJECT_DATA_VERSION,
        uploads: uploadedFiles.length,
        reason: "manual-submit",
      });

      const response = await api.Projects.updateProject(projectId, {
        projectName,
        projectType,
        description: projectDescription,
        totalCostEstimate,
        confidencePercentage,
        updatedBy: user?.zeosUserId ?? 0,
        projectDataJson: JSON.stringify(payload),
        projectDataVersion: PROJECT_DATA_VERSION,
      });

      if (response?.message === "error") {
        throw new Error(response?.message || "Unable to update project.");
      }

      log.info("Project manual details saved", { projectId });
      setStep("memory");
    } catch (err: any) {
      log.error("Manual project update failed", err);
      setError(err?.message || "Unable to update project.");
    } finally {
      setBusy(false);
    }
  };

  const renderStep = () => {
    if (step === "identify") {
      return (
        <div className="space-y-6">
          <div className="bg-white shadow rounded p-6">
            <h2 className="text-xl font-semibold mb-4">
              Tell us about your project
            </h2>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-7">
                <ZeosInput
                  name="projectName"
                  label="Name"
                  placeholder="Building 2 Solar Array"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required={true}
                />
              </div>
              <div className="col-span-12 md:col-span-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                >
                  {PROJECT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-12">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm min-h-[120px]"
                  placeholder="Describe scope, goals, and any constraints."
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded p-6">
            <h3 className="text-lg font-semibold mb-3">
              How do you want to provide project details, goals, and proposals?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setPath("automatic")}
                className={`border rounded-lg p-4 text-left transition ${
                  path === "automatic"
                    ? "border-zeos-orange-dk bg-orange-50"
                    : "border-gray-200"
                }`}
              >
                <div className="text-base font-semibold mb-1">Automatic</div>
                <p className="text-sm text-gray-600">
                  Upload documents and let ZEOS analyze and configure your
                  project data.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setPath("manual")}
                className={`border rounded-lg p-4 text-left transition ${
                  path === "manual"
                    ? "border-zeos-orange-dk bg-orange-50"
                    : "border-gray-200"
                }`}
              >
                <div className="text-base font-semibold mb-1">Manual</div>
                <p className="text-sm text-gray-600">
                  Enter your project information by hand and configure it
                  yourself.
                </p>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <ZeosButton
              label="Back to Incentives"
              type="button"
              mode="prev"
              onPress={handleBackToIncentives}
            />
            <ZeosButton
              label="Continue - Add Project Data"
              type="button"
              mode="action"
              onPress={handleContinue}
              isDisabled={!canContinue}
            />
          </div>
        </div>
      );
    }

    if (step === "automatic") {
      return (
        <div className="space-y-6">
          <div className="bg-white shadow rounded p-6">
            <h2 className="text-xl font-semibold mb-2">
              {projectName || "Project"} → Upload Project Data
            </h2>
            <p className="text-sm text-gray-600">
              Send us project materials to kick off automated analysis.
            </p>
          </div>
          <div className="bg-white shadow rounded p-6">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 lg:col-span-6">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Project Files
                </div>
                {uploadedFiles.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    No files uploaded yet.
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-gray-700">
                    {uploadedFiles.map((uploadedFile, index) => (
                      <div
                        key={`${uploadedFile.fileName}-${index}`}
                        className="flex items-center justify-between border-b border-gray-100 pb-2"
                      >
                        <div>
                          <div className="font-medium">
                            {uploadedFile.fileName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(uploadedFile.sizeBytes / (1024 * 1024)).toFixed(2)}{" "}
                            mb
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-zeos-orange-md">
                          <button
                            type="button"
                            onClick={() =>
                              log.info("View uploaded file (stub)", {
                                fileName: uploadedFile.fileName,
                              })
                            }
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setUploadedFiles((prev) =>
                                prev.filter(
                                  (_, fileIndex) => fileIndex !== index
                                )
                              )
                            }
                          >
                            X
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-12 lg:col-span-6">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Upload Files
                </div>
                {propertyId && projectId && (
                  <ProjectProposalUpload
                    projectId={projectId}
                    propertyId={propertyId}
                    onUploaded={handleUploadMetadataPersist}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <ZeosButton
              label="Back"
              type="button"
              mode="prev"
              onPress={() => setStep("identify")}
            />
            <ZeosButton
              label="Continue - Analyze Data"
              type="button"
              mode="action"
              onPress={handleAnalyzeData}
            />
          </div>
        </div>
      );
    }

    if (step === "manual") {
      return (
        <div className="space-y-6">
          <div className="bg-white shadow rounded p-6">
            <h2 className="text-xl font-semibold mb-2">
              {projectName || "Project"} → Enter Your Project Data
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Add as much information as you can. ZEOS can help estimate
              missing values and improve recommendations.
            </p>
            <div className="grid grid-cols-12 gap-4 border-b border-gray-200 pb-6 mb-6">
              <div className="col-span-12 md:col-span-4">
                <ZeosInput
                  name="systemSize"
                  label="Approximate System Size"
                  placeholder="75 kW"
                  value={manualSystemSize}
                  onChange={(e) => setManualSystemSize(e.target.value)}
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <ZeosInput
                  name="yearInstalled"
                  label="Year Installed"
                  placeholder="2022"
                  value={manualYearInstalled}
                  onChange={(e) => setManualYearInstalled(e.target.value)}
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <ZeosInput
                  name="expectedProduction"
                  label="Expected Annual Production (kWh) or % of Load Offset"
                  placeholder="120,000 kWh"
                  value={manualExpectedProduction}
                  onChange={(e) => setManualExpectedProduction(e.target.value)}
                />
              </div>
              <div className="col-span-12">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List all buildings that will be associated with this project.
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm min-h-[100px]"
                  value={manualBuildings}
                  onChange={(e) => setManualBuildings(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 border-b border-gray-200 pb-6 mb-6">
              <div className="col-span-12 md:col-span-6 space-y-4">
                <div className="text-sm font-semibold text-gray-700">
                  Cost Stack
                </div>
                <ZeosInput
                  name="totalCostEstimate"
                  label="Total Installed Project Cost"
                  placeholder="$"
                  value={manualCostEstimate}
                  onChange={(e) => setManualCostEstimate(e.target.value)}
                />
                <ZeosInput
                  name="incentives"
                  label="Incentives / Rebates Received (or estimated)"
                  placeholder="$"
                  value={manualIncentives}
                  onChange={(e) => setManualIncentives(e.target.value)}
                />
              </div>
              <div className="col-span-12 md:col-span-6 space-y-4">
                <div className="text-sm font-semibold text-gray-700">
                  Performance Intent
                </div>
                <ZeosInput
                  name="performanceGoal"
                  label="Primary Goal (Savings, Compliance, Carbon Reduction, Revenue, Resilience)"
                  placeholder="Savings"
                  value={manualPerformanceGoal}
                  onChange={(e) => setManualPerformanceGoal(e.target.value)}
                />
                <ZeosInput
                  name="usefulLife"
                  label="Expected Useful Life (years)"
                  placeholder="20"
                  value={manualUsefulLife}
                  onChange={(e) => setManualUsefulLife(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-6 space-y-4">
                <div className="text-sm font-semibold text-gray-700">
                  Economic Outcomes (at least one is required)
                </div>
                <ZeosInput
                  name="annualSavings"
                  label="Annual Energy Savings ($ or kWh)"
                  placeholder="$"
                  value={manualAnnualSavings}
                  onChange={(e) => setManualAnnualSavings(e.target.value)}
                />
                <ZeosInput
                  name="simplePayback"
                  label="Simple Payback"
                  placeholder="Years"
                  value={manualSimplePayback}
                  onChange={(e) => setManualSimplePayback(e.target.value)}
                />
                <ZeosInput
                  name="roiIrr"
                  label="ROI / IRR (if known)"
                  placeholder="%"
                  value={manualRoiIrr}
                  onChange={(e) => setManualRoiIrr(e.target.value)}
                />
              </div>
              <div className="col-span-12 md:col-span-6 space-y-4">
                <div className="text-sm font-semibold text-gray-700">
                  Optional
                </div>
                <ZeosInput
                  name="expectedSystemLife"
                  label="Expected System Life"
                  placeholder="Years"
                  value={manualExpectedSystemLife}
                  onChange={(e) => setManualExpectedSystemLife(e.target.value)}
                />
                <ZeosInput
                  name="estimatedAnnualSavings"
                  label="Estimated Annual Savings"
                  placeholder="$"
                  value={manualEstimatedAnnualSavings}
                  onChange={(e) => setManualEstimatedAnnualSavings(e.target.value)}
                />
                <ZeosInput
                  name="optionalRoiIrr"
                  label="ROI / IRR (if known)"
                  placeholder="%"
                  value={manualOptionalRoiIrr}
                  onChange={(e) => setManualOptionalRoiIrr(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <ZeosButton
              label="Back"
              type="button"
              mode="prev"
              onPress={() => setStep("identify")}
            />
            <ZeosButton
              label={busy ? "Saving..." : "Continue - Analyze Data"}
              type="button"
              mode="action"
              onPress={handleManualSubmit}
              isDisabled={busy}
            />
          </div>
        </div>
      );
    }

    if (step === "memory") {
      return (
        <div className="space-y-6">
          <div className="bg-white shadow rounded p-6 space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-2">
                Strategic Recommendations
              </h2>
              <p className="text-sm text-gray-600">
                [Show a summary of the project and its overall goals.]
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Next Logical Steps</h3>
              <p className="text-sm text-gray-600">
                [Show a summary of the project and its overall goals.]
              </p>
            </div>
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-6 space-y-4">
                <ZeosInput
                  name="memoryTotalCost"
                  label="Total Installed Project Cost"
                  placeholder="$"
                  value={manualCostEstimate}
                  onChange={(e) => setManualCostEstimate(e.target.value)}
                />
                <ZeosInput
                  name="memoryIncentives"
                  label="Incentives / Rebates Received (or estimated)"
                  placeholder="$"
                  value={manualIncentives}
                  onChange={(e) => setManualIncentives(e.target.value)}
                />
              </div>
              <div className="col-span-12 md:col-span-6 space-y-4">
                <ZeosInput
                  name="memoryPerformanceGoal"
                  label="Primary Goal (Savings, Compliance, Carbon Reduction, Revenue, Resilience)"
                  placeholder="Savings"
                  value={manualPerformanceGoal}
                  onChange={(e) => setManualPerformanceGoal(e.target.value)}
                />
                <ZeosInput
                  name="memoryUsefulLife"
                  label="Expected Useful Life (years)"
                  placeholder="20"
                  value={manualUsefulLife}
                  onChange={(e) => setManualUsefulLife(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <ZeosButton
              label="Back"
              type="button"
              mode="prev"
              onPress={() => setStep(path === "automatic" ? "automatic" : "manual")}
            />
            <ZeosButton
              label="Complete Survey"
              type="button"
              mode="action"
              onPress={handleCompleteSurvey}
            />
          </div>
        </div>
      );
    }

    const shouldSkipMemory =
      path === "automatic" && uploadedFiles.length > 0;

    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded p-6">
          <h2 className="text-xl font-semibold mb-2">We’ve got your information</h2>
          <p className="text-sm text-gray-600">
            Based on your location and project details, we’ll generate tailored
            recommendations.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <div className="bg-white shadow rounded p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Summary</h3>
                {!aiSummary && (
                  <p className="text-sm text-gray-600">
                    Processing recommendations. Check back shortly.
                  </p>
                )}
                {aiSummary?.status === "pending" && (
                  <p className="text-sm text-gray-600">
                    Processing recommendations. Check back shortly.
                  </p>
                )}
                {aiSummary?.status === "error" && (
                  <p className="text-sm text-red-600">
                    Unable to generate recommendations yet. Try again in a
                    moment.
                  </p>
                )}
                {aiSummaryError && aiSummary?.status !== "error" && (
                  <p className="text-sm text-red-600">{aiSummaryError}</p>
                )}
                {aiSummary?.summaryText && (
                  <p className="text-sm text-gray-700">
                    {aiSummary.summaryText}
                  </p>
                )}
                {aiSummary?.bullets && aiSummary.bullets.length > 0 && (
                  <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
                    {aiSummary.bullets.slice(0, 4).map((bullet, index) => (
                      <li key={`ai-summary-bullet-${index}`}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h4 className="text-base font-semibold mb-2">Next Steps</h4>
                {aiSummary?.nextSteps && aiSummary.nextSteps.length > 0 ? (
                  <ul className="space-y-2 text-sm text-zeos-orange-md list-disc list-inside">
                    {aiSummary.nextSteps.slice(0, 4).map((stepLabel, index) => (
                      <li key={`ai-summary-step-${index}`}>{stepLabel}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="flex flex-col items-start gap-2 text-sm text-zeos-orange-md">
                    <button
                      type="button"
                      onClick={() =>
                        log.info("Next step selected", { step: "battery-survey" })
                      }
                    >
                      Take Battery Energy Storage Survey
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        log.info("Next step selected", { step: "request-proposal" })
                      }
                    >
                      Request Proposal
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white shadow rounded p-6">
              <h4 className="text-base font-semibold mb-4">
                Steps so far
              </h4>
              <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                <li>Project Identification</li>
                <li>Project Data</li>
                {!shouldSkipMemory && <li>ZEOS Project Memory</li>}
                <li>Recommendations</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <ZeosButton
            label="Back"
            type="button"
            mode="prev"
            onPress={() => setStep("memory")}
          />
          <ZeosButton
            label="Back to Projects"
            type="button"
            mode="action"
            onPress={handleBackToProjects}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-zeos-white-md">
      <DashboardHeader companyCallback={setActiveCompany} />
      <main className="flex-grow px-4 py-6 lg:px-6">
        <div className="max-w-[1600px] mx-auto space-y-4">
          <div className="text-sm text-gray-500">{breadcrumb}</div>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-gray-800">
              Add Project
            </h1>
            <div className="text-sm text-gray-500">
              {activeCompany?.companyName}
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          {renderStep()}
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};
